/**
 * Thai Date Utilities
 * Provides functions for Buddhist Era (พ.ศ.) date formatting
 */

export const THAI_MONTHS = [
  '',
  'มกราคม',
  'กุมภาพันธ์',
  'มีนาคม',
  'เมษายน',
  'พฤษภาคม',
  'มิถุนายน',
  'กรกฎาคม',
  'สิงหาคม',
  'กันยายน',
  'ตุลาคม',
  'พฤศจิกายน',
  'ธันวาคม',
];

export const THAI_MONTHS_SHORT = [
  '',
  'ม.ค.',
  'ก.พ.',
  'มี.ค.',
  'เม.ย.',
  'พ.ค.',
  'มิ.ย.',
  'ก.ค.',
  'ส.ค.',
  'ก.ย.',
  'ต.ค.',
  'พ.ย.',
  'ธ.ค.',
];

/**
 * Convert CE year to Buddhist Era (พ.ศ.)
 */
export const toThaiYear = (date: Date): number => {
  return date.getFullYear() + 543;
};

/**
 * Convert Buddhist Era year to CE
 */
export const toCEYear = (thaiYear: number): number => {
  return thaiYear - 543;
};

/**
 * Convert AD year to Buddhist Era (BE) year
 * @param adYear - Gregorian calendar year
 * @returns Buddhist Era year (AD + 543)
 */
export const adToBe = (adYear: number): number => {
  return adYear + 543;
};

/**
 * Convert Buddhist Era (BE) year to AD year
 * @param beYear - Buddhist Era year
 * @returns Gregorian calendar year (BE - 543)
 */
export const beToAd = (beYear: number): number => {
  return beYear - 543;
};

/**
 * Format a Date object to Buddhist Era date string (ISO format with BE year)
 * @param date - Date object or ISO date string
 * @returns Date string in format "YYYY-MM-DD" where YYYY is in Buddhist Era
 */
export const formatDateBE = (date: Date | string | null): string | null => {
  if (!date) return null;
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Check for invalid date
  if (isNaN(dateObj.getTime())) return null;
  
  const beYear = adToBe(dateObj.getFullYear());
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  
  return `${beYear}-${month}-${day}`;
};

/**
 * Parse a Buddhist Era date string to Date object
 * @param dateString - Date string in format "YYYY-MM-DD" where YYYY is in Buddhist Era
 * @returns Date object with Gregorian calendar date, or null if invalid
 */
export const parseDateBE = (dateString: string | null): Date | null => {
  if (!dateString) return null;
  
  // Handle various date formats
  const parts = dateString.includes('-') 
    ? dateString.split('-') 
    : dateString.includes('/') 
    ? dateString.split('/') 
    : null;
  
  if (!parts || parts.length !== 3) return null;
  
  // Parse year, month, day
  const beYear = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  
  // Validate parsed values
  if (isNaN(beYear) || isNaN(month) || isNaN(day)) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  
  // Convert BE year to AD year
  const adYear = beToAd(beYear);
  
  // Create date object (month is 0-indexed in JavaScript Date)
  const date = new Date(adYear, month - 1, day);
  
  // Verify the date is valid (handles invalid dates like Feb 31)
  if (date.getFullYear() !== adYear || 
      date.getMonth() !== month - 1 || 
      date.getDate() !== day) {
    return null;
  }
  
  return date;
};

/**
 * Format date in Thai format: "DD เดือน พ.ศ. YYYY"
 */
export const formatThaiDate = (date: Date | string, short: boolean = false): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const day = dateObj.getDate();
  const month = short ? THAI_MONTHS_SHORT[dateObj.getMonth() + 1] : THAI_MONTHS[dateObj.getMonth() + 1];
  const year = toThaiYear(dateObj);
  return `${day} ${month} ${year}`;
};

/**
 * Format date in Thai short format: "DD/MM/YYYY" (พ.ศ.)
 */
export const formatThaiDateShort = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = toThaiYear(date);
  return `${day}/${month}/${year}`;
};

/**
 * Format date with time in Thai format
 */
export const formatThaiDateTime = (date: Date): string => {
  const dateStr = formatThaiDate(date);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${dateStr} ${hours}:${minutes} น.`;
};

/**
 * Parse Thai date string (DD/MM/YYYY in พ.ศ.) to Date object
 */
export const parseThaiDate = (dateStr: string): Date | null => {
  const parts = dateStr.split('/');
  if (parts.length !== 3) return null;

  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const thaiYear = parseInt(parts[2], 10);
  const ceYear = toCEYear(thaiYear);

  if (isNaN(day) || isNaN(month) || isNaN(ceYear)) return null;

  return new Date(ceYear, month, day);
};

/**
 * Format number in Thai format with comma separators
 */
export const formatThaiNumber = (num: number | null | undefined, decimals: number = 2): string => {
  if (num === null || num === undefined || isNaN(num)) {
    return '0.00';
  }
  return num.toLocaleString('th-TH', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format currency in Thai Baht
 */
export const formatThaiCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '0.00 บาท';
  }
  return `${formatThaiNumber(amount, 2)} บาท`;
};

/**
 * Get current date in Thai format
 */
export const getCurrentThaiDate = (): string => {
  return formatThaiDate(new Date());
};

/**
 * Get Thai day of week
 */
export const getThaiDayOfWeek = (date: Date): string => {
  const days = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'];
  return days[date.getDay()];
};

/**
 * Get Thai day of week (short form)
 */
export const getThaiDayOfWeekShort = (date: Date): string => {
  const days = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];
  return days[date.getDay()];
};
